"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import Image from "next/image";
gsap.registerPlugin(ScrollTrigger);

export default function ScrollAnimation() {
  const router = useRouter();
  const locale = useLocale();
  const l = useLocale();
  const t = useTranslations('ShopSection');
  
  const spotlightRef = useRef<HTMLDivElement>(null);
  const projectIndexRef = useRef<HTMLHeadingElement>(null);
  const projectImagesRef = useRef<HTMLDivElement>(null);
  const projectNamesRef = useRef<HTMLDivElement>(null);
  const lenisRef = useRef<Lenis | null>(null);

  // Get localized field
  const getLocalizedField = (item: any, field: string) => 
    item[`${field}_${l}`] || item[`${field}_en`];

  const cat = [
    {
      id: "1",
      image_en: '/bag/padel-bag-on-a-table-with-a-padel-ball.avif',
      image_ar: '/bag/حقيبة-بادل-على-طاولة-مع-كرة-بادل.avif',
      image_de: '/bag/padel-tasche-auf-einem-tisch-mit-padel-ball.avif',
      image_es: '/bag/bolsa-de-padel-sobre-una-mesa-con-una-pelota-de-padel.avif',
      image_fr: '/bag/sac-de-padel-sur-une-table-avec-une-balle-de-padel.avif',
      image_it: '/bag/borsa-da-padel-su-un-tavolo-con-una-palla-da-padel.avif',
      image_nl: '/bag/padeltas-op-een-tafel-met-een-padelbal.avif',
      image_pt: '/bag/bolsa-de-padel-sobre-uma-mesa-com-uma-bola-de-padel.avif',
      image_ru: '/bag/sumka-dlya-padel-na-stole-s-myachom-dlya-padel.avif',
      image_sv: '/bag/padelvaska-pa-ett-bord-med-en-padelboll.avif',
      title: t('title-0'),
      category: 'bgs'
    },
    {
      id: "2",
      image_en: '/child/child-padel-player-practicing-with-a-racket.avif',
      image_ar: '/child/طفل-لاعب-بادل-يتدرب-بمضرب.avif',
      image_de: '/child/kind-padel-spieler-trainiert-mit-schlaeger.avif',
      image_es: '/child/nino-jugador-de-padel-practicando-con-una-raqueta.avif',
      image_fr: '/child/enfant-joueur-de-padel-s-entrainant-avec-une-raquette.avif',
      image_it: '/child/bambino-giocatore-di-padel-che-si-allena-con-una-racchetta.avif',
      image_nl: '/child/kind-padelspeler-die-trainiert-met-een-racket.avif',
      image_pt: '/child/crianca-jogador-de-padel-treinando-com-uma-raquete.avif',
      image_ru: '/child/rebenok-igrok-v-padel-treniruyushchiysya-s-raketkoy.avif',
      image_sv: '/child/barn-padelspelare-som-tranar-med-racket.avif',
      title: t('title-1'),
      category: 'jn'
    },
    {
      id: "3",
      image_en: '/racket/girl-carrying-a-padel-racket.avif',
      image_ar: '/racket/فتاة-تحمل-مضرب-بادل.avif',
      image_de: '/racket/madchen-mit-padel-schlaeger.avif',
      image_es: '/racket/chica-llevando-una-raqueta-de-padel.avif',
      image_fr: '/racket/fille-portant-une-raquette-de-padel.avif',
      image_it: '/racket/ragazza-che-trasporta-una-racchetta-da-padel.avif',
      image_nl: '/racket/meisje-met-padelracket.avif',
      image_pt: '/racket/menina-carregando-uma-raquete-de-padel.avif',
      image_ru: '/racket/devushka-s-raketkoy-dlya-padel.avif',
      image_sv: '/racket/flicka-som-bar-en-padelracket.avif',
      title: t('title-2'),
      category: 'rkb,rki,rkp'
    },
    {
      id: "4",
      image_en: '/shoes/shoes.avif',
      image_ar: '/shoes/احذية.avif',
      image_de: '/shoes/schuhe.avif',
      image_es: '/shoes/zapatos.avif',
      image_fr: '/shoes/chaussures.avif',
      image_it: '/shoes/scarpe.avif',
      image_nl: '/shoes/schoenen.avif',
      image_pt: '/shoes/sapatos.avif',
      image_ru: '/shoes/obuv.avif',
      image_sv: '/shoes/skor.avif',
      title: t('title-3'),
      category: 'sw,sm'
    },
    {
      id: "5",
      image_en: '/glasses/girl-wearing-sunglasses-for-sports.avif',
      image_ar: '/glasses/fatat-labisat-nazarat-shamsiya-lilriyada.avif',
      image_de: '/glasses/madchen-mit-sonnenbrille-fuer-sport.avif',
      image_es: '/glasses/chica-llevando-gafas-de-sol-para-deporte.avif',
      image_fr: '/glasses/fille-portant-lunettes-de-soleil-pour-sport.avif',
      image_it: '/glasses/ragazza-con-occhiali-da-sole-per-sport.avif',
      image_nl: '/glasses/meisje-met-zonnebril-voor-sport.avif',
      image_pt: '/glasses/menina-usando-oculos-de-sol-para-esporte.avif',
      image_ru: '/glasses/devushka-v-solnechnykh-ochkakh-dlya-sporta.avif',
      image_sv: '/glasses/flicka-med-solglasogon-for-sport.avif',
      title: t('title-4'),
      category: 'sung'
    },
    {
      id: "6",
      image_en: '/men/man-in-a-clothing-store.avif',
      image_ar: '/men/rajul-fi-mahalla-malabis.avif',
      image_de: '/men/mann-in-einem-bekleidungsgeschäft.avif',
      image_es: '/men/hombre-en-una-tienda-de-ropa.avif',
      image_fr: '/men/homme-dans-un-magasin-de-vetements.avif',
      image_it: '/men/uomo-in-un-negozio-di-abbigliamento.avif',
      image_nl: '/men/man-in-kledingwinkel.avif',
      image_pt: '/men/homem-em-uma-loja-de-roupas.avif',
      image_ru: '/men/muzhchina-v-magazine-odezhdy.avif',
      image_sv: '/men/man-i-en-kladbutik.avif',
      title: t('title-5'),
      category: 'tsm,swsm,sockm,srtm,psm,lptsm,legm,jcktm,capm'
    },
    {
      id: "7",
      image_en: '/watter/sports-water-bottle.avif',
      image_ar: '/watter/qara-maa-riyadi.avif',
      image_de: '/watter/sport-wasserflasche.avif',
      image_es: '/watter/botella-de-agua-deportiva.avif',
      image_fr: '/watter/bouteille-d-eau-sportive.avif',
      image_it: '/watter/bottiglia-d-acqua-sportiva.avif',
      image_nl: '/watter/sport-waterfles.avif',
      image_pt: '/watter/garrafa-de-agua-esportiva.avif',
      image_ru: '/watter/sportivnaya-vodochnaya-butylka.avif',
      image_sv: '/watter/sportvattenflaska.avif',
      title: t('title-6'),
      category: 'wb'
    },
    {
      id: "8",
      image_en: '/woman/girl-in-a-clothing-store.avif',
      image_ar: '/woman/fatat-fi-mahalla-malabis.avif',
      image_de: '/woman/madchen-in-einem-bekleidungsgeschäft.avif',
      image_es: '/woman/chica-en-una-tienda-de-ropa.avif',
      image_fr: '/woman/fille-dans-un-magasin-de-vetements.avif',
      image_it: '/woman/ragazza-in-un-negozio-di-abbigliamento.avif',
      image_nl: '/woman/meisje-in-kledingwinkel.avif',
      image_pt: '/woman/menina-em-uma-loja-de-roupas.avif',
      image_ru: '/woman/devushka-v-magazine-odezhdy.avif',
      image_sv: '/woman/flicka-i-en-kladbutik.avif',
      title: t('title-7'),
      category: 'drs,vsr,top,tswm,swsw,sockw,srtw,psw,lptsw,legw,jcktw,capw'
    },
  ];

  const handleNavigate = (category: string) => {
    router.push(`/${locale}/p?q=${encodeURIComponent(category)}`);
  };

  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis();
    lenisRef.current = lenis;

    lenis.on("scroll", ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    // Setup scroll animation
    const spotlightSection = spotlightRef.current;
    const projectIndex = projectIndexRef.current;
    const projectImagesContainer = projectImagesRef.current;
    const projectNamesContainer = projectNamesRef.current;

    if (
      !spotlightSection ||
      !projectIndex ||
      !projectImagesContainer ||
      !projectNamesContainer
    )
      return;

    const projectImgs = projectImagesContainer.querySelectorAll(".project-img");
    const projectNames = projectNamesContainer.querySelectorAll("p");
    const totalProjectCount = projectNames.length;

    const spotlightSectionHeight = spotlightSection.offsetHeight;
    const spotlightSectionPadding = 32; // 2rem = 32px
    const projectIndexHeight = projectIndex.offsetHeight;
    const containerHeight = projectNamesContainer.offsetHeight;
    const imagesHeight = projectImagesContainer.offsetHeight;

    const moveDistanceIndex =
      spotlightSectionHeight - spotlightSectionPadding * 2 - projectIndexHeight;
    const moveDistanceNames =
      spotlightSectionHeight - spotlightSectionPadding * 2 - containerHeight;
    const moveDistanceImages = window.innerHeight - imagesHeight;
    const imgActivationThreshold = window.innerHeight / 2;

    ScrollTrigger.create({
      trigger: spotlightSection,
      start: "top top",
      end: `+=${window.innerHeight * 5}px`,
      pin: true,
      pinSpacing: true,
      scrub: 1,
      onUpdate: (self) => {
        const progress = self.progress;
        const currentIndex = Math.min(
          Math.floor(progress * totalProjectCount) + 1,
          totalProjectCount
        );

        projectIndex.textContent = `${String(currentIndex).padStart(
          2,
          "0"
        )}/${String(totalProjectCount).padStart(2, "0")}`;

        gsap.set(projectIndex, {
          y: progress * moveDistanceIndex,
        });

        gsap.set(projectImagesContainer, {
          y: progress * moveDistanceImages,
        });

        projectImgs.forEach((img) => {
          const imgRect = img.getBoundingClientRect();
          const imgTop = imgRect.top;
          const imgBottom = imgRect.bottom;

          if (
            imgTop <= imgActivationThreshold &&
            imgBottom >= imgActivationThreshold
          ) {
            gsap.set(img, {
              opacity: 1,
            });
          } else {
            gsap.set(img, {
              opacity: 0.5,
            });
          }
        });

        projectNames.forEach((p, index) => {
          const startProgress = index / totalProjectCount;
          const endProgress = (index + 1) / totalProjectCount;
          const projectProgress = Math.max(
            0,
            Math.min(
              1,
              (progress - startProgress) / (endProgress - startProgress)
            )
          );

          gsap.set(p, {
            y: -projectProgress * moveDistanceNames,
          });

          if (projectProgress > 0 && projectProgress < 1) {
            gsap.set(p, {
              color: "#000",
            });
          } else {
            gsap.set(p, {
              color: "#4a4a4a",
            });
          }
        });
      },
    });

    // Cleanup
    return () => {
      lenis.destroy();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [cat]);

  return (
    <div className="bg-yel text-black min-h-screen">
      {/* Spotlight Section */}
      <section
        ref={spotlightRef}
        className="relative w-full h-screen p-8 overflow-hidden"
      >
        {/* Project Index */}
      <div className="project-index pointer-events-none ltr-text">
  <h1
    ref={projectIndexRef}
    className="uppercase text-5xl md:text-7xl lg:text-9xl font-semibold leading-none will-change-transform"
  >
    01/{String(cat.length).padStart(2, "0")}
  </h1>
</div>

        {/* Project Images */}
        <div
          ref={projectImagesRef}
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-[35%] py-[50vh] flex flex-col gap-2 lg:gap-2 -z-10 will-change-transform"
        >
          {cat.map((item, index) => (
     <div
  key={item.id}
  className="relative h-96 w-full rounded-lg aspect-video opacity-50 transition-all duration-300 overflow-hidden cursor-pointer hover:opacity-100"
  onClick={() => handleNavigate(item.category)}
>
  <Image
    src={getLocalizedField(item, "image")}
    alt={item.title}
    fill
    className="object-cover"
    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
  />
</div>
          ))}
        </div>

        {/* Project Names */}
        <div
          ref={projectNamesRef}
          className="absolute right-4 md:right-8 bottom-8 flex flex-col items-end gap-1 pointer-events-auto"
        >
          {cat.map((item, index) => (
            <p
              key={item.id}
              onClick={() => handleNavigate(item.category)}
              className="text-lg md:text-xl lg:text-2xl font-extrabold uppercase leading-tight text-[#4a4a4a] transition-colors duration-300 will-change-transform cursor-pointer hover:text-black whitespace-nowrap"
            >
              {item.title}
            </p>
          ))}
        </div>
      </section>
    </div>
  );
}